import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared editorial chrome for guides, answer pages and blog posts, so every
 * long-form page carries the same navy-and-brass treatment as the home page:
 * an uppercase eyebrow, a display headline, a brass hairline, and hairline
 * bordered panels on card stock rather than heavy tinted blocks.
 */

export function ArticleEyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function ArticleHeader({
  backTo,
  eyebrow,
  title,
  lede,
  meta,
  className,
}: {
  /** Rendered "back to index" link. */
  backTo?: ReactNode;
  eyebrow: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("border-b border-border pb-8", className)}>
      {backTo ? <div className="mb-6 text-sm">{backTo}</div> : null}
      <ArticleEyebrow>{eyebrow}</ArticleEyebrow>
      <h1 className="mt-4 font-display text-3xl font-semibold leading-[1.12] tracking-tight sm:text-4xl">
        {title}
      </h1>
      <span aria-hidden="true" className="mt-5 block h-px w-16 bg-brass" />
      {lede ? <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{lede}</p> : null}
      {meta ? <p className="mt-4 text-xs text-muted-foreground">{meta}</p> : null}
    </header>
  );
}

export function SectionHeading({
  children,
  className,
  as: Tag = "h2",
}: {
  children: ReactNode;
  className?: string;
  as?: "h2" | "h3";
}) {
  return (
    <Tag
      className={cn(
        Tag === "h2"
          ? "font-display text-2xl font-semibold tracking-tight"
          : "font-display text-lg font-semibold tracking-tight",
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function EditorialPanel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card p-6 shadow-[var(--shadow-plate)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Brass step marker used by "what to do next" style ordered lists. */
export function StepMarker({ index }: { index: number }) {
  return (
    <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/10 font-mono text-xs font-semibold text-brass">
      {index}
    </span>
  );
}
